import { Add, Delete } from '@mui/icons-material';
import { Button } from '@src/components/ui/button/button';
import { TextInput } from '@src/components/ui/react-hook-form/text-Input/text-input';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CreateBillingAddress } from './create-billing-address';
import { useQuery } from '@tanstack/react-query';
import { axios } from '@src/lib/axios';
import { AutoCompleteInput } from '@src/components/ui/react-hook-form/autocomplete-input/autocomplete-input';
import { queryClient } from '@src/lib/query-client';
import { cn } from '@src/utils/ui';
import { ConfirmationDialog } from '@src/components/common/dialogs/confirmation-dialog';
import { IconButton } from '@mui/material';
import { PhoneInput } from '@src/components/common/phone-input/phone-input';
import { useFormContext } from 'react-hook-form';

const formatAddressOptions = (options = []) =>
    options.map((val, index) => ({
        index,
        label: [val.Name, val.Line1, val.State, val.City, val.PostCode].filter(Boolean).join(', '),
        value: val
    }));

export const QuoteContactDetails = props => {
    const { watch, setValue } = useFormContext();
    const { buyerBillingAddresses, updateSelectedAddress, permissions } = props;
    const [billingToDelete, setBillingToDelete] = useState(null);
    const [billingDeleteLoading, setBillingDeleteLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const PhoneNumber = watch('PhoneNumber') || '';

    const { data } = useQuery({
        queryKey: 'billingAddress',
        queryFn: async () => {
            const result = await axios.get('/users/address');
            return formatAddressOptions(result.data || []);
        },
        initialData: () => formatAddressOptions(buyerBillingAddresses)
    });

    const handleDelete = option => {
        if (permissions.isAuthorizedToDelete) setBillingToDelete(option.value);
    };

    const doDeleteAddress = async () => {
        setBillingDeleteLoading(true);
        try {
            await axios.post('/users/address/delete', { addressId: billingToDelete.ID });
            queryClient.refetchQueries({ queryKey: 'billingAddress', exact: true });
            setBillingToDelete(null);
        } catch (e) {
            console.error(e.message);
        }
        setBillingDeleteLoading(false);
    };

    const handleInputChange = val => {
        setValue('PhoneNumber', val, {
            shouldValidate: val.replace(/\s/g, '').length === 10
        });
    };

    return (
        <>
            <div className='tw-space-y-[12px]'>
                <div>
                    <b className='tw-font-medium'>First Name</b>
                    <TextInput
                        name='FirstName'
                        variant='outlined'
                        className='tw-w-full'
                        InputProps={{
                            className: 'tw-text-[16px] tw-bg-white'
                        }}
                    />
                </div>
                <div>
                    <b className='tw-font-medium'>Last Name</b>
                    <TextInput
                        name='LastName'
                        variant='outlined'
                        className='tw-w-full'
                        InputProps={{
                            className: 'tw-text-[16px] tw-bg-white'
                        }}
                    />
                </div>
                <div>
                    <b className='tw-font-medium'>Phone Number</b>
                    <PhoneInput
                        name='PhoneNumber'
                        variant='outlined'
                        className='tw-w-full'
                        InputProps={{
                            className: 'tw-text-[16px] tw-bg-white'
                        }}
                        value={PhoneNumber}
                        onChange={handleInputChange}
                        label={null}
                        isRHF
                    />
                </div>
                <div>
                    <b className='tw-font-medium'>Email</b>
                    <TextInput
                        name='Email'
                        variant='outlined'
                        className='tw-w-full'
                        InputProps={{
                            className: 'tw-text-[16px] tw-bg-white'
                        }}
                    />
                </div>
                <div>
                    <b className='tw-font-medium'>Task Address</b>
                    <AutoCompleteInput
                        by='ID'
                        name='BillingAddress'
                        options={data}
                        className='tw-w-full'
                        onChange={({ target }) => updateSelectedAddress(target.value.ID, true)}
                        renderOption={(props, option, state) => (
                            <li
                                {...props}
                                className='hover:tw-bg-gray-100 tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-w-full tw-text-[16px] tw-px-6 tw-py-3'
                            >
                                <span>{option.label}</span>
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleDelete(option);
                                    }}
                                    className={cn([option.index === 0 ? 'invisible' : ''])}
                                >
                                    <Delete size={18} weight='fill' className='tw-text-gray-600' />
                                </IconButton>
                            </li>
                        )}
                    />
                </div>
                <Button variant='contained' startIcon={<Add />} onClick={() => setOpen(true)}>
                    ADD NEW ADDRESS
                </Button>
            </div>
            <CreateBillingAddress open={open} onClose={() => setOpen(false)} {...props} />
            <ConfirmationDialog
                open={!!billingToDelete}
                onClose={() => setBillingToDelete(null)}
                onAgree={doDeleteAddress}
                loading={billingDeleteLoading}
                content={{
                    body: 'Are you sure want to delete?'
                }}
            />
        </>
    );
};

QuoteContactDetails.propTypes = {
    buyerBillingAddresses: PropTypes.arrayOf(
        PropTypes.shape({
            ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            Name: PropTypes.string,
            Line1: PropTypes.string,
            Country: PropTypes.string,
            State: PropTypes.string,
            City: PropTypes.string,
            PostCode: PropTypes.string
        })
    ).isRequired,
    updateSelectedAddress: PropTypes.func.isRequired,
    // Props for CreateBillingAddress
    user: PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        Email: PropTypes.string.isRequired
    }).isRequired,
    validatePermissionToPerformAction: PropTypes.func.isRequired,
    permissions: PropTypes.object
};
